/**
 * Logique, mode « Quantificateurs » : « pour tout » et « il existe » sur un
 * ensemble E écrit en entier ; chaque élément est vérifié, le contre-exemple
 * (ou l'exemple) est montré, la négation s'écrit en échangeant ∀ et ∃. En
 * prépa, deux variables : la grille x × y montre pourquoi l'ordre des
 * quantificateurs compte.
 */

import { useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { Note, Segmented } from '../../ui/controls';
import { fr } from './fr';
import {
  DOMAINS,
  GRID,
  NEG_ORDER,
  ORDER_TEX,
  PREDS,
  PREDS2,
  order2,
  quantTex,
  quantVerdict,
  type Order,
  type Quant,
} from './logic';

const minus = (n: number) => String(n).replace('-', '−');

function PredChips<T extends { id: string; plain: string }>({
  preds,
  value,
  onChange,
}: {
  preds: readonly T[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div role="group" aria-label="Propriété" className="flex flex-wrap gap-2">
      {preds.map((p) => (
        <button
          key={p.id}
          type="button"
          aria-pressed={p.id === value}
          onClick={() => onChange(p.id)}
          className={cn(
            'min-h-11 rounded-xl border px-3 text-sm font-semibold',
            p.id === value
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-ink-200 text-ink-700 hover:bg-ink-50',
          )}
        >
          {p.plain}
        </button>
      ))}
    </div>
  );
}

function OneVariable() {
  const [q, setQ] = useState<Quant>('all');
  const [dom, setDom] = useState('Z');
  const [pid, setPid] = useState('sq-gt');
  const d = DOMAINS.find((x) => x.id === dom)!;
  const p = PREDS.find((x) => x.id === pid)!;
  const v = quantVerdict(q, p, d);
  const negQ: Quant = q === 'all' ? 'exists' : 'all';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Segmented
          ariaLabel="Quantificateur"
          value={q}
          options={[
            { value: 'all', label: 'Pour tout ∀' },
            { value: 'exists', label: 'Il existe ∃' },
          ]}
          onChange={setQ}
        />
        <Segmented
          ariaLabel="Ensemble E"
          value={dom}
          options={[
            { value: 'N', label: 'E de 0 à 10' },
            { value: 'Z', label: 'E de −5 à 5' },
          ]}
          onChange={setDom}
        />
      </div>
      <PredChips preds={PREDS} value={pid} onChange={setPid} />

      <section
        className="border-ink-100 space-y-3 rounded-2xl border px-3 py-2"
        data-testid="lg-quant"
      >
        <MathText className="text-ink-900 block overflow-x-auto">{`$${d.tex}$`}</MathText>
        <MathText className="text-ink-900 block overflow-x-auto text-lg">{`$${quantTex(q, p)}$`}</MathText>
        <ul className="flex flex-wrap gap-1.5" aria-label="Chaque élément de E">
          {d.values.map((n, i) => {
            const ok = v.marks[i]!;
            const key = n === v.witness;
            return (
              <li
                key={n}
                aria-label={`${minus(n)} : ${ok ? 'vérifie' : 'ne vérifie pas'} la propriété`}
                className={cn(
                  'flex h-11 min-w-11 flex-col items-center justify-center rounded-xl px-1 text-sm font-bold tabular-nums',
                  ok ? 'bg-success-50 text-success-700' : 'bg-error-50 text-error-700',
                  key && 'ring-primary-500 ring-2',
                )}
              >
                <span>{minus(n)}</span>
                <span aria-hidden className="text-xs leading-none">
                  {ok ? '✓' : '✗'}
                </span>
              </li>
            );
          })}
        </ul>
        <Note tone={v.truth ? 'success' : 'error'}>
          {fr(
            q === 'all'
              ? v.truth
                ? `Vraie : chaque élément de E vérifie ${p.plain}.`
                : `Fausse : n = ${minus(v.witness!)} ne vérifie pas ${p.plain}. Un seul contre-exemple suffit.`
              : v.truth
                ? `Vraie : n = ${minus(v.witness!)} convient. Un seul exemple suffit.`
                : `Fausse : aucun élément de E ne vérifie ${p.plain}. Il faut les essayer tous.`,
          )}
        </Note>
      </section>

      <section
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
        data-testid="lg-neg"
      >
        <p className="text-ink-600 text-sm font-semibold">Négation</p>
        <MathText className="text-ink-900 block overflow-x-auto">
          {`$\\text{non}\\left(${quantTex(q, p)}\\right) \\iff ${quantTex(negQ, p, true)}$`}
        </MathText>
        <p className="text-ink-700 text-sm">
          {fr(
            `On échange « pour tout » et « il existe », et on nie la propriété. La négation est ${v.truth ? 'fausse' : 'vraie'} : toujours le contraire de la phrase.`,
          )}
        </p>
      </section>
    </div>
  );
}

const ORDERS: ReadonlyArray<{ value: Order; label: string }> = [
  { value: 'AE', label: '∀x ∃y' },
  { value: 'EA', label: '∃y ∀x' },
  { value: 'AA', label: '∀x ∀y' },
  { value: 'EE', label: '∃x ∃y' },
];

function TwoVariables() {
  const [o, setO] = useState<Order>('AE');
  const [pid, setPid] = useState('opp');
  const p = PREDS2.find((x) => x.id === pid)!;
  const v = order2(o, p);
  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Ordre des quantificateurs" value={o} options={ORDERS} onChange={setO} />
      <PredChips preds={PREDS2} value={pid} onChange={setPid} />
      <section
        className="border-ink-100 space-y-3 rounded-2xl border px-3 py-2"
        data-testid="lg-quant2"
      >
        <MathText className="text-ink-900 block overflow-x-auto">{`$F = \\{-3,\\ \\dots,\\ 3\\}$`}</MathText>
        <MathText className="text-ink-900 block overflow-x-auto text-lg">{`$${ORDER_TEX[o]}${p.tex}$`}</MathText>
        <div className="overflow-x-auto">
          <table className="mx-auto border-separate border-spacing-0.5 text-center text-sm">
            <caption className="sr-only">
              Cases où {p.plain} est vraie : lignes x, colonnes y
            </caption>
            <thead>
              <tr>
                <th scope="col" className="text-ink-500 px-1 text-xs">
                  x \ y
                </th>
                {GRID.map((y) => (
                  <th
                    key={y}
                    scope="col"
                    className={cn(
                      'text-ink-700 h-8 w-9 rounded-md text-xs font-bold',
                      v.cols.includes(y) && 'bg-primary-50 text-primary-700',
                    )}
                  >
                    {minus(y)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GRID.map((x) => (
                <tr key={x}>
                  <th
                    scope="row"
                    className={cn(
                      'text-ink-700 h-9 w-9 rounded-md text-xs font-bold',
                      v.rows.includes(x) && 'bg-primary-50 text-primary-700',
                    )}
                  >
                    {minus(x)}
                  </th>
                  {GRID.map((y) => {
                    const ok = p.test(x, y);
                    return (
                      <td
                        key={y}
                        aria-label={`x = ${minus(x)}, y = ${minus(y)} : ${ok ? 'vraie' : 'fausse'}`}
                        className={cn(
                          'h-9 w-9 rounded-md text-xs font-bold',
                          ok ? 'bg-success-500 text-white' : 'bg-ink-50 text-ink-300',
                          // la ligne fautive (∀x ∃y faux) : ses cases fausses en rouge
                          v.rows.includes(x) && !ok && 'bg-error-50 text-error-700',
                          // la case témoin (contre-exemple ou exemple)
                          v.cell && v.cell[0] === x && v.cell[1] === y && 'ring-primary-500 ring-2',
                        )}
                      >
                        {ok ? '✓' : '·'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Note tone={v.truth ? 'success' : 'error'}>
          {fr(`${v.truth ? 'Vraie' : 'Fausse'}. ${v.why}`)}
        </Note>
        {v.note && <Note live={false}>{fr(v.note)}</Note>}
      </section>
      <section className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2">
        <p className="text-ink-600 text-sm font-semibold">Négation</p>
        <MathText className="text-ink-900 block overflow-x-auto">{`$${NEG_ORDER[o]}${p.neg}$`}</MathText>
        <p className="text-ink-700 text-sm">
          {fr('Chaque ∀ devient ∃, chaque ∃ devient ∀, dans le même ordre, et on nie la fin.')}
        </p>
      </section>
    </div>
  );
}

export function QuantMode({ rank }: { rank: number }) {
  const [two, setTwo] = useState(false);
  return (
    <div className="space-y-4">
      {rank >= 8 && (
        <Segmented
          ariaLabel="Nombre de variables"
          value={two ? 'two' : 'one'}
          options={[
            { value: 'one', label: 'Une variable' },
            { value: 'two', label: 'Deux variables' },
          ]}
          onChange={(v) => setTwo(v === 'two')}
        />
      )}
      {two && rank >= 8 ? <TwoVariables /> : <OneVariable />}
    </div>
  );
}
